/**
 * Shape-aware nesting on an occupancy grid.
 *
 * maxrects packs BOUNDING BOXES, so a C-shaped collar reserves its whole box
 * and nothing can sit in its opening; two sleeves can never interleave. Here
 * every piece is rasterised from its cut silhouette (dilated by half the
 * piece padding plus the laser-outline extent, so gaps and outlines stay
 * physical) onto a grid of CELL_MM cells, in every orientation the factory
 * allows (0° always; 180° and 90° when listed; 270° when both are).
 *
 * Placement is bottom-left on the grid ("lowest, then leftmost": the sheet
 * fills from its top edge, which is where the roll starts), with a complete
 * scan of every column — a narrow gap between two curved panels is found, as
 * is a hole inside an existing layout (a sleeve cap dropping into the V the
 * two bodies' armholes form).
 *
 * Per jersey, which piece goes next, in which orientation and at which of its
 * few best positions, is chosen by a beam search: every partial layout is
 * scored by completing it greedily and measuring the used paper length (the
 * objective: fewest sheets, then shortest used length); the best partial
 * layouts survive each step. The best complete layouts are then compacted on
 * a finer grid (every piece pushed up / left / sideways-and-up as far as it
 * goes) to win back the coarse grid's quantisation slack, and the shortest
 * wins. The coarse masks are built FROM the fine ones, so a coarse layout is
 * always valid on the fine grid.
 *
 * A whole order (hundreds of pieces) is packed greedily (several orders, best
 * kept), each sheet compacted on the fine grid, pieces from later sheets
 * pulled into the room that opens up, and the last sheet re-packed by beam
 * search.
 *
 * The output is the same NestResult the sheet builders consume: xPt/yPt are
 * the top-left of the piece's ROTATED boxed frame (piece + outline extent),
 * `rotation` says how the piece is turned about that frame.
 */
import { flattenPathData, IDENTITY } from './clipFlatten';
import ClipperLib from 'clipper-lib';
import {
  DEFAULT_PIECE_PADDING_IN,
  SHEET_EDGE_MARGIN_MM,
  outlineExtentPt,
  resolveOutlineSpec,
  type NestPieceInput,
  type NestPlacement,
  type NestResult,
  type NestSettings,
  type NestSheet,
  type NestEffort,
  type NestRotation,
  type PackOptions,
  allowedRotations,
  packPieces,
  garmentGroups,
  garmentLabel,
} from './nesting';
import { inchToPt, mmToPt } from './units';
import { signedArea, type Point, type Polygon } from './labelPath';
import { verifySheet, type SheetViolation } from './nestVerify';

/** Search grid resolution. */
export const CELL_MM = 3;
/** Compaction grid resolution (must divide CELL_MM). */
export const FINE_CELL_MM = 1;
const FINE_RATIO = Math.round(CELL_MM / FINE_CELL_MM);
const SCALE = 1000;
/** Extra growth of the rasterised halo (pt): a curve can bulge up to ~0.4 pt
 *  between the raster's scanlines, so without it two masks that do not touch
 *  could still be a hair closer than the padding. The exact check (nestVerify)
 *  guards the result either way. */
export const RASTER_SAFETY_PT = 0.5;
/** Margin for the retry when the exact check still finds a collision. */
const RETRY_SAFETY_PT = 3;
/** Fine-grid cell cap (a grid costs ~4 bytes per cell). The settings allow
 *  paper up to 400 x 4000 in; past this the packer works with a shorter
 *  length per sheet (pieces continue on the next sheet) instead of running
 *  the tab out of memory. 63 x 118 in paper is ~4.8M cells. */
const FINE_MAX_CELLS = 12_000_000;

/** One beam-search run: beam width, candidate positions per (piece,
 *  orientation), and whether completions also try best-fit order. Width 0 =
 *  no search, only the completions of the empty sheet. */
export interface BeamConfig {
  width: number;
  pockets: number;
  bestFit: boolean;
}

/**
 * Beam-search effort, richest first. Beam search is not monotone in its width
 * (a wider beam can push out the partial layout that led to the best
 * result), so the top rung is a small portfolio whose best result wins. A
 * group takes the richest rung whose estimated work fits BEAM_BUDGET (a
 * 5-piece jersey with 0/180 gets the top rung; a 25-piece hockey jersey a
 * narrow beam; a huge group only the greedy completions).
 */
export const BEAM_LADDER: ReadonlyArray<readonly BeamConfig[]> = [
  [
    { width: 8, pockets: 4, bestFit: true },
    { width: 24, pockets: 6, bestFit: true },
  ],
  [{ width: 8, pockets: 4, bestFit: true }],
  [{ width: 4, pockets: 2, bestFit: true }],
  [{ width: 4, pockets: 2, bestFit: false }],
  [{ width: 4, pockets: 1, bestFit: false }],
  [{ width: 2, pockets: 1, bestFit: false }],
  [{ width: 1, pockets: 1, bestFit: false }],
];
/** Work budget per sheet group, in estimated bottom-left searches (~20 us
 *  each on a laptop, so ~2-3 s). */
export const BEAM_BUDGET = 120_000;

/** Estimated work of one beam run over n pieces of `types` distinct shapes
 *  with `orients` orientations each: bottom-left searches, each weighted by
 *  n / 5 (a search on a sheet holding more pieces scans more rows past more
 *  obstacles; calibrated on 5-piece jerseys). */
function estimateBeam(n: number, types: number, orients: number, c: BeamConfig): number {
  const weight = Math.max(1, n / 5);
  let total = 0;
  for (let d = 0; d < n; d++) {
    const children = (d === 0 ? 1 : c.width) * Math.min(types, n - d) * orients * c.pockets;
    const rest = n - d - 1;
    const completion = rest * orients * (c.bestFit ? 1 + Math.min(types, rest) : 1);
    total += children * (completion + 1);
  }
  return total * weight;
}

/** The richest ladder rung that fits the budget; below the ladder, the
 *  plain completions (best-fit only when affordable). */
export function pickPortfolio(
  n: number,
  types: number,
  orients: number,
  from = 0,
  budget = BEAM_BUDGET,
  ladder: ReadonlyArray<readonly BeamConfig[]> = BEAM_LADDER
): readonly BeamConfig[] {
  for (const rung of ladder.slice(from)) {
    const work = rung.reduce((t, c) => t + estimateBeam(n, types, orients, c), 0);
    if (work <= budget) return rung;
  }
  const bestFitWork = n * n * types * orients * Math.max(1, n / 5);
  return [{ width: 0, pockets: 0, bestFit: bestFitWork <= budget }];
}

/** Whole orders above this many pieces get fewer greedy variants. */
const BIG_ORDER = 400;
/** Whole order: the last sheet is re-packed from this ladder rung down. */
const LAST_SHEET_RUNG = 1;

/**
 * How much searching one run may do — the user's speed-versus-paper choice.
 *  - groupBudget: beam budget of one jersey (or one sheet group).
 *  - orderBudget: per-jersey mode, shared by ALL distinct jerseys of the order
 *    (a 12-size order must not cost 12 x groupBudget); floor minGroupBudget.
 *  - blocksBudget: whole order, the jersey-blocks candidate, shared likewise.
 *  - pool: complete layouts compacted per sheet (shortest wins).
 *  - promoteRounds: whole order, retries with the last sheet's kinds first.
 *  - deadlineMs: safety net — past it, optional search is skipped.
 *  - ladder: beam configurations, richest first.
 */
export interface EffortProfile {
  groupBudget: number;
  orderBudget: number;
  minGroupBudget: number;
  blocksBudget: number;
  pool: number;
  promoteRounds: number;
  deadlineMs: number;
  ladder: ReadonlyArray<readonly BeamConfig[]>;
  /** A jersey that spills a short tail onto an extra sheet although it could
   *  fit is searched again with this level's FULL jersey budget (the order
   *  budget is split across sizes). Fast rescues with the balanced search:
   *  speed must not cost whole sheets. */
  rescueWith: NestEffort;
}

export const EFFORT_PROFILES: Record<NestEffort, EffortProfile> = {
  fast: {
    groupBudget: BEAM_BUDGET / 8,
    orderBudget: BEAM_BUDGET,
    minGroupBudget: BEAM_BUDGET / 40,
    blocksBudget: BEAM_BUDGET / 4,
    pool: 2,
    promoteRounds: 1,
    deadlineMs: 8_000,
    ladder: BEAM_LADDER,
    rescueWith: 'balanced',
  },
  balanced: {
    groupBudget: BEAM_BUDGET,
    orderBudget: 6 * BEAM_BUDGET,
    minGroupBudget: BEAM_BUDGET / 8,
    blocksBudget: 2 * BEAM_BUDGET,
    pool: 6,
    promoteRounds: 3,
    deadlineMs: 25_000,
    ladder: BEAM_LADDER,
    rescueWith: 'balanced',
  },
  tightest: {
    groupBudget: 4 * BEAM_BUDGET,
    orderBudget: 16 * BEAM_BUDGET,
    minGroupBudget: BEAM_BUDGET / 2,
    blocksBudget: 6 * BEAM_BUDGET,
    pool: 10,
    promoteRounds: 4,
    deadlineMs: 60_000,
    // one richer rung on top: a wider, deeper beam for small jerseys
    ladder: [
      [
        { width: 8, pockets: 4, bestFit: true },
        { width: 24, pockets: 6, bestFit: true },
        { width: 48, pockets: 8, bestFit: true },
      ],
      ...BEAM_LADDER,
    ],
    rescueWith: 'tightest',
  },
};
/** Whole order: pieces are pulled back only from the last few sheets (the
 *  point is to empty or shorten the tail; pulling from every sheet into every
 *  earlier one grows with sheets squared). */
const PULL_SOURCES = 3;
/**
 * Safety net for pathological orders: once the packer has run this long, the
 * remaining OPTIONAL search (further beam steps, alternative candidates,
 * pull-backs, compaction) is skipped and the best layout so far is used.
 * Normal orders finish well inside it, so their results never depend on the
 * clock.
 */
export const SOFT_DEADLINE_MS = EFFORT_PROFILES.balanced.deadlineMs;
/** Effort levels, cheapest first. A level also runs the searches of every
 *  cheaper level (scaled to its budget) and keeps the best, so moving towards
 *  'tightest' never makes the nest worse — beam search alone is not monotone
 *  in its width. */
export const EFFORT_ORDER: readonly NestEffort[] = ['fast', 'balanced', 'tightest'];

/** Thrown when PackOptions.isCancelled says the user gave up waiting. */
export class NestCancelledError extends Error {
  constructor() {
    super('Nesting cancelled');
    this.name = 'NestCancelledError';
  }
}

type Rotation = NestRotation;

/** Rasterised footprint of one piece in one orientation. */
export interface PieceMask {
  /** Cells wide / tall (slot = boxed frame + half padding each side). */
  w: number;
  h: number;
  /** Number of occupied cells. */
  count: number;
  /** Per-row [start, end) runs of occupied cells. */
  rows: Array<Array<[number, number]>>;
  /** Flat runs: runs[rowOff[r] .. rowOff[r + 1]) = s0, e0, s1, e1, … */
  rowOff: Int32Array;
  runs: Int16Array;
  rotation: Rotation;
  rotated: boolean;
  /** Slot origin → boxed-frame origin offset (half padding), pt. */
  slotInsetPt: number;
  /** Rotated slot size, pt (exact, not rounded to cells). */
  slotWPt: number;
  slotHPt: number;
  /** Row that failed the last collision check (checked first next time). */
  hint: number;
}

/**
 * Occupancy grid, one pair of lookup rows per grid row (W + 1 entries each,
 * entry W is a sentinel): `nxt[c]` = first occupied column ≥ c (W when none),
 * `nfr[c]` = first free column ≥ c. "Does run [s, e) fit at x" is one lookup,
 * and a failing lookup says exactly where the next candidate x is (past the
 * whole occupied block), so a row scan costs one check per obstacle.
 */
class OccGrid {
  readonly W: number;
  readonly H: number;
  readonly S: number;
  nxt: Int16Array;
  nfr: Int16Array;
  rowFree: Int32Array;

  /** identity[c] = c (template for resetting pointer rows). */
  readonly identity: Int16Array;

  constructor(W: number, H: number, src?: OccGrid) {
    this.W = W;
    this.H = H;
    this.S = W + 1;
    this.identity = src?.identity ?? Int16Array.from({ length: W + 1 }, (_, c) => c);
    if (src) {
      this.nxt = src.nxt.slice();
      this.nfr = src.nfr.slice();
      this.rowFree = src.rowFree.slice();
    } else {
      this.nxt = new Int16Array(this.S * H).fill(W);
      this.nfr = new Int16Array(this.S * H);
      for (let row = 0; row < H; row++) this.nfr.set(this.identity, row * this.S);
      this.rowFree = new Int32Array(H).fill(W);
    }
  }

  clone(): OccGrid {
    return new OccGrid(this.W, this.H, this);
  }

  /** Overwrite this grid with `src` (same size) without allocating. */
  copyFrom(src: OccGrid): this {
    this.nxt.set(src.nxt);
    this.nfr.set(src.nfr);
    this.rowFree.set(src.rowFree);
    return this;
  }

  /** -1 when the mask fits at (x, y), else the next x worth trying (> x). */
  check(m: PieceMask, x: number, y: number): number {
    const { nxt, nfr, S } = this;
    const { rowOff, runs } = m;
    const hint = m.hint;
    {
      const base = (y + hint) * S;
      for (let k = rowOff[hint], end = rowOff[hint + 1]; k < end; k += 2) {
        const s = runs[k];
        const n = nxt[base + x + s];
        if (n < x + runs[k + 1]) return nfr[base + n] - s;
      }
    }
    for (let r = 0; r < m.h; r++) {
      if (r === hint) continue;
      const end = rowOff[r + 1];
      let k = rowOff[r];
      if (k === end) continue;
      const base = (y + r) * S;
      for (; k < end; k += 2) {
        const s = runs[k];
        const n = nxt[base + x + s];
        if (n < x + runs[k + 1]) {
          m.hint = r;
          return nfr[base + n] - s;
        }
      }
    }
    return -1;
  }

  /** For a mask that fits at (x, y): how many columns further right it still fits. */
  slack(m: PieceMask, x: number, y: number): number {
    const { nxt, S } = this;
    const { rowOff, runs } = m;
    let d = this.W - (x + m.w);
    for (let r = 0; r < m.h && d > 0; r++) {
      const base = (y + r) * S + x;
      for (let k = rowOff[r], end = rowOff[r + 1]; k < end; k += 2) {
        const room = nxt[base + runs[k]] - (x + runs[k + 1]);
        if (room < d) d = room;
      }
    }
    return d;
  }

  fits(m: PieceMask, x: number, y: number): boolean {
    if (x < 0 || y < 0 || x + m.w > this.W || y + m.h > this.H) return false;
    return this.check(m, x, y) < 0;
  }

  /** Leftmost x ≤ xMax where the mask fits in row y, or -1. */
  firstFit(m: PieceMask, y: number, xMax: number): number {
    let x = 0;
    while (x <= xMax) {
      const j = this.check(m, x, y);
      if (j < 0) return x;
      x = j;
    }
    return -1;
  }

  /**
   * Lowest-then-leftmost fit. With `tol` > 0, positions up to `tol` rows
   * below the lowest fit compete on x (leftmost wins).
   */
  findBL(m: PieceMask, tol = 0): { x: number; y: number } | null {
    const { W, H, rowFree } = this;
    if (m.w > W || m.h > H) return null;
    const need = m.count;
    let band = 0;
    for (let r = 0; r < m.h; r++) band += rowFree[r];
    let best: { x: number; y: number } | null = null;
    for (let y = 0; y + m.h <= H; y++) {
      if (y > 0) band += rowFree[y + m.h - 1] - rowFree[y - 1];
      if (best && y > best.y + tol) break;
      if (band < need) continue;
      const xMax = best ? best.x - 1 : W - m.w;
      const x = this.firstFit(m, y, xMax);
      if (x < 0) continue;
      best = { x, y };
      if (x === 0) break;
    }
    return best;
  }

  /**
   * Distinct candidate positions ("pockets"): for every column the lowest row
   * the mask fits in (looking at most `range` rows below the lowest fit
   * overall), then the local minima of that profile — each flat stretch
   * contributes its left and right end. Sorted lowest first, then leftmost.
   */
  findPockets(m: PieceMask, range: number, max: number): Array<{ x: number; y: number }> {
    const first = this.findBL(m, 0);
    if (!first) return [];
    const { W, H } = this;
    const nx = W - m.w + 1;
    const ymin = new Int32Array(nx).fill(-1);
    let open = nx;
    for (let y = first.y; y + m.h <= H && y <= first.y + range && open > 0; y++) {
      let x = 0;
      while (x < nx) {
        if (ymin[x] >= 0) {
          x++;
          continue;
        }
        const j = this.check(m, x, y);
        if (j < 0) {
          // every x up to x + slack fits too
          const end = Math.min(nx - 1, x + this.slack(m, x, y));
          for (; x <= end; x++) {
            if (ymin[x] < 0) {
              ymin[x] = y;
              open--;
            }
          }
        } else x = j;
      }
    }
    const out: Array<{ x: number; y: number }> = [];
    let x = 0;
    while (x < nx) {
      if (ymin[x] < 0) {
        x++;
        continue;
      }
      let e = x;
      while (e + 1 < nx && ymin[e + 1] === ymin[x]) e++;
      const y = ymin[x];
      const leftOk = x === 0 || ymin[x - 1] < 0 || ymin[x - 1] > y;
      const rightOk = e === nx - 1 || ymin[e + 1] < 0 || ymin[e + 1] > y;
      if (leftOk && rightOk) {
        out.push({ x, y });
        if (e !== x) out.push({ x: e, y });
      }
      x = e + 1;
    }
    out.sort((a, b) => a.y - b.y || a.x - b.x);
    return out.slice(0, max);
  }

  /** Set (fill = true) or clear the mask's cells at (x, y). */
  private paint(m: PieceMask, x: number, y: number, fill: boolean): void {
    const { nxt, nfr, S, rowFree, identity } = this;
    const { rowOff, runs } = m;
    for (let r = 0; r < m.h; r++) {
      const k0 = rowOff[r];
      const k1 = rowOff[r + 1];
      if (k0 === k1) continue;
      const row = y + r;
      const base = row * S;
      // right to left over the runs; columns ≥ the last run's end are unchanged
      let prevA = x + runs[k1 - 1];
      let next = nxt[base + prevA];
      let nf = nfr[base + prevA];
      let changed = 0;
      for (let k = k1 - 2; k >= k0; k -= 2) {
        const a = x + runs[k];
        const b = x + runs[k + 1];
        // gap [b, prevA): occupancy unchanged, pointers may change
        for (let c = prevA - 1; c >= b; c--) {
          if (nxt[base + c] === c) {
            nfr[base + c] = nf;
            next = c;
          } else {
            nxt[base + c] = next;
            nf = c;
          }
        }
        // free cells inside [a, b) before the change
        let free = 0;
        for (let c = nfr[base + a]; c < b;) {
          const e = Math.min(nxt[base + c], b);
          free += e - c;
          c = e < b ? nfr[base + e] : b;
        }
        if (fill) {
          changed += free;
          nxt.set(identity.subarray(a, b), base + a);
          nfr.fill(nf, base + a, base + b);
          next = a;
        } else {
          changed += b - a - free;
          nfr.set(identity.subarray(a, b), base + a);
          nxt.fill(next, base + a, base + b);
          nf = a;
        }
        prevA = a;
      }
      rowFree[row] += fill ? -changed : changed;
      // left of the span: a free stretch needs the new "next occupied", an
      // occupied stretch the new "next free"; both stop at the first change
      let c = prevA - 1;
      if (c >= 0 && nxt[base + c] === c) {
        for (; c >= 0 && nxt[base + c] === c; c--) nfr[base + c] = nf;
      } else {
        for (; c >= 0 && nxt[base + c] !== c; c--) nxt[base + c] = next;
      }
    }
  }

  stamp(m: PieceMask, x: number, y: number): void {
    this.paint(m, x, y, true);
  }

  unstamp(m: PieceMask, x: number, y: number): void {
    this.paint(m, x, y, false);
  }
}

/**
 * Conservative scanline rasterisation (even-odd over all rings) of polygons
 * given in slot pt coordinates: a cell is occupied when the shape touches ANY
 * part of it (three scanlines per row, floor/ceil bounds), so two masks that
 * do not overlap on the grid are truly at least the dilation apart.
 */
function rasterise(
  polygons: Polygon[],
  w: number,
  h: number,
  cellPt: number
): Array<Array<[number, number]>> {
  const rows: Array<Array<[number, number]>> = [];
  const EPS = 1e-6;
  // edges bucketed by the first row whose scanlines they can cross
  const buckets: Array<Array<[number, number, number, number]>> = Array.from(
    { length: h },
    () => []
  );
  for (const poly of polygons) {
    for (let i = 0, n = poly.length; i < n; i++) {
      const [x1, y1] = poly[i];
      const [x2, y2] = poly[(i + 1) % n];
      if (y1 === y2) continue;
      const lo = Math.min(y1, y2);
      const row = Math.max(0, Math.floor(lo / cellPt));
      if (row < h) buckets[row].push([x1, y1, x2, y2]);
    }
  }
  let active: Array<[number, number, number, number]> = [];
  const xs: number[] = [];
  const spans: Array<[number, number]> = [];
  for (let row = 0; row < h; row++) {
    spans.length = 0;
    for (const e of buckets[row]) active.push(e);
    const rowTop = row * cellPt;
    active = active.filter((e) => Math.max(e[1], e[3]) > rowTop);
    for (const frac of [0.02, 0.5, 0.98]) {
      const yc = (row + frac) * cellPt;
      xs.length = 0;
      for (const [x1, y1, x2, y2] of active) {
        if (yc >= Math.min(y1, y2) && yc < Math.max(y1, y2)) {
          xs.push(x1 + ((yc - y1) * (x2 - x1)) / (y2 - y1));
        }
      }
      xs.sort((a, b) => a - b);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        const a = Math.max(0, Math.floor(xs[i] / cellPt + EPS));
        const b = Math.min(w, Math.ceil(xs[i + 1] / cellPt - EPS));
        if (b > a) spans.push([a, b]);
      }
    }
    spans.sort((p, q) => p[0] - q[0]);
    const runs: Array<[number, number]> = [];
    for (const [a, b] of spans) {
      const last = runs[runs.length - 1];
      if (last && a <= last[1]) last[1] = Math.max(last[1], b);
      else runs.push([a, b]);
    }
    rows.push(runs);
  }
  return rows;
}

/** Dilate polygons by `deltaPt` with Clipper (round joins). */
function dilate(polygons: Polygon[], deltaPt: number): Polygon[] {
  if (deltaPt <= 0) return polygons;
  try {
    const subject = polygons.map((p) =>
      p.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) }))
    );
    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
    const unioned: Array<Array<{ X: number; Y: number }>> = [];
    clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      unioned,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );
    const offsetter = new ClipperLib.ClipperOffset(2, 0.2 * SCALE);
    offsetter.AddPaths(unioned, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
    const out: Array<Array<{ X: number; Y: number }>> = [];
    offsetter.Execute(out, deltaPt * SCALE);
    const result = out
      .filter((p) => p.length >= 3)
      .map((p) => p.map((pt) => [pt.X / SCALE, pt.Y / SCALE] as Point));
    return result.length ? result : polygons;
  } catch {
    return polygons;
  }
}

/** Piece silhouette (or its box) as polygons in piece-local pt. */
async function pieceOutlinePolygons(piece: NestPieceInput): Promise<Polygon[]> {
  if (piece.outlineD) {
    const flat = await flattenPathData(piece.outlineD, IDENTITY, 0.3);
    const polys = (flat ?? [])
      .filter((p) => p.points.length >= 3)
      .map((p) => p.points.map(([x, y]) => [x, y] as Point));
    if (polys.length && polys.some((p) => Math.abs(signedArea(p)) > 1)) return polys;
  }
  return [
    [
      [0, 0],
      [piece.widthPt, 0],
      [piece.widthPt, piece.heightPt],
      [0, piece.heightPt],
    ],
  ];
}

interface SlotShape {
  polys: Polygon[];
  slotW: number;
  slotH: number;
}

/** Dilated silhouette in slot coordinates (slot = boxed frame + padding/2). */
async function slotPolygons(
  piece: NestPieceInput,
  extentPt: number,
  paddingPt: number,
  safetyPt = RASTER_SAFETY_PT
): Promise<SlotShape> {
  const base = await pieceOutlinePolygons(piece);
  const grow = extentPt + paddingPt / 2;
  const shifted = base.map((p) => p.map(([x, y]) => [x + grow, y + grow] as Point));
  return {
    // (the slot keeps its exact size: a halo never reaches past it, and the
    // safety growth past the slot edge is clipped, which is harmless there)
    polys: dilate(shifted, grow + safetyPt),
    slotW: piece.widthPt + 2 * grow,
    slotH: piece.heightPt + 2 * grow,
  };
}

function makeMasks(
  shape: SlotShape,
  rotations: Rotation[],
  paddingPt: number,
  cellMm: number
): PieceMask[] {
  const cellPt = mmToPt(cellMm);
  const { polys, slotW, slotH } = shape;
  return rotations.map((rotation) => {
    // Rotate about the slot so the rotated slot's top-left stays at the origin
    // (same convention as the sheet builders' placement frame).
    const map: (p: Point) => Point =
      rotation === 90
        ? ([x, y]) => [slotH - y, x]
        : rotation === 180
          ? ([x, y]) => [slotW - x, slotH - y]
          : rotation === 270
            ? ([x, y]) => [y, slotW - x]
            : (p) => p;
    const quarter = rotation === 90 || rotation === 270;
    const wPt = quarter ? slotH : slotW;
    const hPt = quarter ? slotW : slotH;
    const rp = polys.map((p) => p.map(map));
    const w = Math.max(1, Math.ceil(wPt / cellPt - 1e-9));
    const h = Math.max(1, Math.ceil(hPt / cellPt - 1e-9));
    return maskFromRows(rasterise(rp, w, h, cellPt), w, {
      rotation,
      rotated: quarter,
      slotInsetPt: paddingPt / 2,
      slotWPt: wPt,
      slotHPt: hPt,
    });
  });
}

/** Build a mask from per-row runs. */
function maskFromRows(
  rows: Array<Array<[number, number]>>,
  w: number,
  proto: Pick<PieceMask, 'rotation' | 'rotated' | 'slotInsetPt' | 'slotWPt' | 'slotHPt'>
): PieceMask {
  const h = rows.length;
  let count = 0;
  const rowOff = new Int32Array(h + 1);
  const flat: number[] = [];
  rows.forEach((runs, r) => {
    rowOff[r] = flat.length;
    for (const [s, e] of runs) {
      flat.push(s, e);
      count += e - s;
    }
  });
  rowOff[h] = flat.length;
  return {
    ...proto,
    w,
    h,
    count,
    rows,
    rowOff,
    runs: Int16Array.from(flat),
    hint: Math.floor(h / 2),
  };
}

/**
 * Coarse mask covering a fine one: a coarse cell is occupied when any of its
 * ratio x ratio fine cells is. So a coarse layout without overlaps is also one
 * on the fine grid (positions x ratio), exactly.
 */
function coarsen(fine: PieceMask, ratio: number): PieceMask {
  const w = Math.ceil(fine.w / ratio);
  const h = Math.ceil(fine.h / ratio);
  const rows: Array<Array<[number, number]>> = [];
  for (let cy = 0; cy < h; cy++) {
    const spans: Array<[number, number]> = [];
    for (let fy = cy * ratio; fy < Math.min(fine.h, (cy + 1) * ratio); fy++) {
      for (const [s, e] of fine.rows[fy]) spans.push([Math.floor(s / ratio), Math.ceil(e / ratio)]);
    }
    spans.sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [];
    for (const [s, e] of spans) {
      const last = merged[merged.length - 1];
      if (last && s <= last[1]) last[1] = Math.max(last[1], e);
      else merged.push([s, e]);
    }
    rows.push(merged);
  }
  return maskFromRows(rows, w, fine);
}

/**
 * Masks for one piece in every allowed orientation. Slot = boxed frame grown
 * by half the padding on each side; the silhouette is dilated by extent +
 * padding/2 so two masks touching means the outlines are exactly `padding`
 * apart.
 */
export async function buildMasks(
  piece: NestPieceInput,
  extentPt: number,
  paddingPt: number,
  allow90: boolean,
  allow180 = false,
  cellMm = CELL_MM
): Promise<PieceMask[]> {
  const shape = await slotPolygons(piece, extentPt, paddingPt);
  const rotations = allowedRotations({
    allowed_rotations: [...(allow90 ? [90] : []), ...(allow180 ? [180] : [])],
  });
  return makeMasks(shape, rotations, paddingPt, cellMm);
}

/** Positions within this many mm of the lowest fit count as "equally low";
 *  among them the leftmost wins (the whole-order greedy tries both). */
export const BLF_Y_TOLERANCE_MM = 20;
const BLF_Y_TOLERANCE_CELLS = Math.round(BLF_Y_TOLERANCE_MM / CELL_MM);

// ---------------------------------------------------------------------------
// search

interface Prepared {
  piece: NestPieceInput;
  /** Geometry key: identical keys have identical masks. */
  key: string;
  /** Small integer per distinct key. */
  kid: number;
  masks: PieceMask[];
  fine: PieceMask[];
  /** Occupied cells of the upright mask (area proxy). */
  area: number;
}

interface Placed {
  item: Prepared;
  mi: number;
  x: number;
  y: number;
}

interface Layout {
  placed: Placed[];
  unplaced: Prepared[];
}

/** Sort key of a layout: nothing left over, then shortest used length. */
interface Score {
  leftArea: number;
  length: number;
}

const better = (a: Score, b: Score) =>
  a.leftArea !== b.leftArea ? a.leftArea < b.leftArea : a.length < b.length;

const coarseMask = (p: Placed) => p.item.masks[p.mi];
const fineMask = (p: Placed) => p.item.fine[p.mi];

function usedLengthPt(placed: Placed[], cellPt: number, maskOf: (p: Placed) => PieceMask): number {
  if (!placed.length) return 0;
  let top = Infinity;
  let bottom = -Infinity;
  for (const p of placed) {
    const m = maskOf(p);
    top = Math.min(top, p.y * cellPt);
    bottom = Math.max(bottom, p.y * cellPt + m.slotHPt);
  }
  return bottom - top;
}

function usedLengthCells(placed: Placed[], maskOf: (p: Placed) => PieceMask): number {
  if (!placed.length) return 0;
  let top = Infinity;
  let bottom = -Infinity;
  for (const p of placed) {
    const m = maskOf(p);
    if (p.y < top) top = p.y;
    if (p.y + m.h > bottom) bottom = p.y + m.h;
  }
  return bottom - top;
}

/** Best orientation for one piece: lowest bottom edge, then leftmost. */
function bestPlacement(
  g: OccGrid,
  item: Prepared,
  tol: number,
  masks: PieceMask[] = item.masks
): { mi: number; x: number; y: number } | null {
  let best: { mi: number; x: number; y: number } | null = null;
  let bestBottom = Infinity;
  for (let mi = 0; mi < masks.length; mi++) {
    const m = masks[mi];
    const pos = g.findBL(m, tol);
    if (!pos) continue;
    const bottom = pos.y + m.h;
    if (!best || bottom < bestBottom || (bottom === bestBottom && pos.x < best.x)) {
      best = { mi, ...pos };
      bestBottom = bottom;
    }
  }
  return best;
}

/** Greedy completion: pieces in the given order, bottom-left. Mutates g. */
function greedyFill(g: OccGrid, items: Prepared[], tol: number): Layout {
  const placed: Placed[] = [];
  const unplaced: Prepared[] = [];
  for (const item of items) {
    const b = bestPlacement(g, item, tol);
    if (!b) {
      unplaced.push(item);
      continue;
    }
    g.stamp(item.masks[b.mi], b.x, b.y);
    placed.push({ item, ...b });
  }
  return { placed, unplaced };
}

/**
 * Best-fit completion: at every step the (piece, orientation) whose
 * bottom-left position ends highest goes next (ties: larger piece). Mutates g.
 */
function bestFitFill(g: OccGrid, items: Prepared[]): Layout {
  const placed: Placed[] = [];
  const unplaced: Prepared[] = [];
  let left = [...items];
  while (left.length) {
    let pick = -1;
    let pos: { mi: number; x: number; y: number } | null = null;
    let pickBottom = Infinity;
    const seen = new Set<number>();
    const dead = new Set<number>();
    for (let i = 0; i < left.length; i++) {
      const item = left[i];
      if (seen.has(item.kid)) continue;
      seen.add(item.kid);
      const b = bestPlacement(g, item, 0);
      if (!b) {
        dead.add(item.kid);
        continue;
      }
      const bottom = b.y + item.masks[b.mi].h;
      if (bottom < pickBottom) {
        pick = i;
        pos = b;
        pickBottom = bottom;
      }
    }
    if (dead.size) {
      for (const item of left) if (dead.has(item.kid)) unplaced.push(item);
      const chosen = pick >= 0 ? left[pick] : null;
      left = left.filter((item) => !dead.has(item.kid));
      pick = chosen ? left.indexOf(chosen) : -1;
    }
    if (!pos || pick < 0) break;
    const item = left[pick];
    g.stamp(item.masks[pos.mi], pos.x, pos.y);
    placed.push({ item, ...pos });
    left.splice(pick, 1);
  }
  return { placed, unplaced: [...unplaced, ...left] };
}

function scoreOf(layout: Layout): Score {
  return {
    leftArea: layout.unplaced.reduce((s, it) => s + it.area, 0),
    length: usedLengthCells(layout.placed, coarseMask),
  };
}

interface BeamState {
  grid: OccGrid;
  placed: Placed[];
  /** Remaining items, largest first. */
  left: Prepared[];
  tags: string[];
}

/**
 * Beam search over (next piece type, orientation, position) for one sheet.
 * Each child places one more piece at one of the `pockets` best positions for
 * it (see findPockets) and is scored by completing the layout two ways —
 * largest-first bottom-left and best-fit — and taking the shorter; the best
 * `width` distinct children survive each step. Returns the best few distinct
 * complete layouts seen (every completion is a candidate).
 */
async function beamSheet(
  W: number,
  H: number,
  items: Prepared[],
  cfg: BeamConfig,
  maybeYield: Pause,
  poolSize: number,
  stop: () => boolean = () => false
): Promise<Layout[]> {
  const sorted = [...items].sort((a, b) => b.area - a.area);
  const root: BeamState = { grid: new OccGrid(W, H), placed: [], left: sorted, tags: [] };
  // the best few distinct complete layouts seen (compaction picks among them)
  const pool: Array<{ layout: Layout; score: Score; sig: string }> = [];
  const offer = (layout: Layout) => {
    const score = scoreOf(layout);
    if (pool.length >= poolSize && !better(score, pool[pool.length - 1].score)) return score;
    const sig = layout.placed
      .map((p) => `${p.item.kid}:${p.mi}:${p.x}:${p.y}`)
      .sort()
      .join('|');
    if (pool.some((e) => e.sig === sig)) return score;
    pool.push({ layout, score, sig });
    pool.sort((a, b) => (better(a.score, b.score) ? -1 : better(b.score, a.score) ? 1 : 0));
    if (pool.length > poolSize) pool.pop();
    return score;
  };
  offer(greedyFill(root.grid.clone(), sorted, 0));
  if (cfg.width === 0) {
    // No search affordable: a few cheap constructive orders instead of one
    // (each is a single pass over the pieces, far below one beam step).
    const byArea = (p: Prepared, q: Prepared) => q.area - p.area;
    const orders = [
      (p: Prepared, q: Prepared) => q.masks[0].h - p.masks[0].h || byArea(p, q),
      (p: Prepared, q: Prepared) => q.masks[0].w - p.masks[0].w || byArea(p, q),
      (p: Prepared, q: Prepared) =>
        Math.max(q.masks[0].w, q.masks[0].h) - Math.max(p.masks[0].w, p.masks[0].h) || byArea(p, q),
    ];
    offer(greedyFill(root.grid.clone(), sorted, BLF_Y_TOLERANCE_CELLS));
    for (const order of orders) {
      await maybeYield();
      const items2 = [...items].sort(order);
      offer(greedyFill(root.grid.clone(), items2, 0));
      offer(greedyFill(root.grid.clone(), items2, BLF_Y_TOLERANCE_CELLS));
    }
    if (cfg.bestFit) offer(bestFitFill(root.grid.clone(), sorted));
    return pool.map((e) => e.layout);
  }
  const { width, pockets } = cfg;
  let beam: BeamState[] = [root];
  const scratch = new OccGrid(W, H);
  while (beam.length && beam[0].left.length) {
    // (the pool already holds complete layouts: stopping early is safe)
    if (stop()) break;
    const children: Array<{
      parent: BeamState;
      li: number;
      mi: number;
      x: number;
      y: number;
      tags: string[];
      score: Score;
      partial: number;
    }> = [];
    const seen = new Set<string>();
    for (const st of beam) {
      const tried = new Set<string>();
      for (let li = 0; li < st.left.length; li++) {
        const item = st.left[li];
        if (tried.has(item.key)) continue;
        tried.add(item.key);
        for (let mi = 0; mi < item.masks.length; mi++) {
          const m = item.masks[mi];
          const spots = st.grid.findPockets(m, m.h, pockets);
          for (const pos of spots) {
            const tags = [...st.tags, `${item.kid}:${mi}:${pos.x}:${pos.y}`].sort();
            const sig = tags.join('|');
            if (seen.has(sig)) continue;
            seen.add(sig);
            const grid = scratch.copyFrom(st.grid);
            grid.stamp(m, pos.x, pos.y);
            const placed = [...st.placed, { item, mi, x: pos.x, y: pos.y }];
            const left = st.left.filter((_, k) => k !== li);
            const rest = greedyFill(grid, left, 0);
            let score = offer({ placed: [...placed, ...rest.placed], unplaced: rest.unplaced });
            if (cfg.bestFit && left.length > 1) {
              const g2 = scratch.copyFrom(st.grid);
              g2.stamp(m, pos.x, pos.y);
              const rest2 = bestFitFill(g2, left);
              const s2 = offer({ placed: [...placed, ...rest2.placed], unplaced: rest2.unplaced });
              if (better(s2, score)) score = s2;
            }
            children.push({
              parent: st,
              li,
              mi,
              x: pos.x,
              y: pos.y,
              tags,
              score,
              partial: usedLengthCells(placed, coarseMask),
            });
            await maybeYield();
          }
        }
      }
    }
    children.sort((a, b) =>
      better(a.score, b.score) ? -1 : better(b.score, a.score) ? 1 : a.partial - b.partial
    );
    // only the survivors get their own grid
    beam = children.slice(0, width).map((c) => {
      const item = c.parent.left[c.li];
      const grid = c.parent.grid.clone();
      grid.stamp(item.masks[c.mi], c.x, c.y);
      return {
        grid,
        placed: [...c.parent.placed, { item, mi: c.mi, x: c.x, y: c.y }],
        left: c.parent.left.filter((_, k) => k !== c.li),
        tags: c.tags,
      };
    });
  }
  return pool.map((e) => e.layout);
}

/**
 * How far the mask (fitting at x, y) can move by (dx, dy) steps: galloping
 * then bisecting. Every returned distance is a verified fit (a jump across a
 * thin obstacle is fine — only the end position matters).
 */
function slide(g: OccGrid, m: PieceMask, x: number, y: number, dx: number, dy: number): number {
  const ok = (d: number) => {
    const nx = x + dx * d;
    const ny = y + dy * d;
    return nx >= 0 && ny >= 0 && g.check(m, nx, ny) < 0;
  };
  if (!ok(1)) return 0;
  let good = 1;
  let bad = 2;
  while (ok(bad)) {
    good = bad;
    bad *= 2;
  }
  while (bad - good > 1) {
    const mid = (good + bad) >> 1;
    if (ok(mid)) good = mid;
    else bad = mid;
  }
  return good;
}

/** Awaited between steps of long loops (lets the UI repaint). */
type Pause = () => Promise<void>;

/** Push every piece up, then left, as far as it goes; repeat until nothing
 *  moves. Positions are cells of g (fine masks). Mutates g and the positions. */
async function squeeze(g: OccGrid, placed: Placed[], pause: Pause): Promise<void> {
  for (let round = 0; round < 3; round++) {
    placed.sort((a, b) => a.y - b.y || a.x - b.x);
    for (let pass = 0; pass < 4; pass++) {
      let moved = false;
      for (const p of placed) {
        const m = fineMask(p);
        g.unstamp(m, p.x, p.y);
        for (let guard = 0; guard < 50; guard++) {
          const up = slide(g, m, p.x, p.y, 0, -1);
          p.y -= up;
          const left = slide(g, m, p.x, p.y, -1, 0);
          p.x -= left;
          if (!up && !left) break;
          moved = true;
        }
        g.stamp(m, p.x, p.y);
        await pause();
      }
      if (!moved) break;
    }
    if (!(await shake(g, placed, pause))) break;
  }
}

/** Sideways search range of the shake step, fine cells. */
const SHAKE_RANGE = 40;

/**
 * Shake: every piece tries the columns within SHAKE_RANGE of where it sits,
 * sliding up from each, and moves to the one that ends highest. Lowest
 * pieces first (they decide the used length). True when anything moved up.
 */
async function shake(g: OccGrid, placed: Placed[], pause: Pause): Promise<boolean> {
  let improved = false;
  const order = [...placed].sort((a, b) => b.y + fineMask(b).h - (a.y + fineMask(a).h));
  for (const p of order) {
    const m = fineMask(p);
    g.unstamp(m, p.x, p.y);
    let bestX = p.x;
    let bestY = p.y;
    for (let d = 1; d <= SHAKE_RANGE; d++) {
      for (const x of [p.x - d, p.x + d]) {
        if (x < 0 || x + m.w > g.W || g.check(m, x, p.y) >= 0) continue;
        const y = p.y - slide(g, m, x, p.y, 0, -1);
        if (y < bestY) {
          bestY = y;
          bestX = x;
        }
      }
    }
    if (bestY < p.y) {
      p.x = bestX;
      p.y = bestY;
      improved = true;
    }
    g.stamp(m, p.x, p.y);
    await pause();
  }
  return improved;
}

/** Stamp a coarse layout onto a fine grid (positions x FINE_RATIO). Null when
 *  the fine raster rejects a coarse position (then the coarse layout stands). */
function toFine(g: OccGrid, layout: Placed[]): Placed[] | null {
  const out = layout.map((p) => ({ ...p, x: p.x * FINE_RATIO, y: p.y * FINE_RATIO }));
  for (const p of out) {
    if (!g.fits(fineMask(p), p.x, p.y)) return null;
    g.stamp(fineMask(p), p.x, p.y);
  }
  return out;
}

/**
 * Re-place a coarse layout on the fine grid and squeeze it. Positions become
 * FINE cells; null when the fine raster rejects a coarse position.
 */
async function compact(
  W: number,
  H: number,
  layout: Placed[],
  pause: Pause
): Promise<Placed[] | null> {
  // pieces only move up / left: the grid never needs to reach below the
  // lowest coarse position
  let bottom = 0;
  for (const p of layout) bottom = Math.max(bottom, (p.y + coarseMask(p).h) * FINE_RATIO);
  const g = new OccGrid(W, Math.min(H, bottom + FINE_RATIO));
  const out = toFine(g, layout);
  if (!out) return null;
  await squeeze(g, out, pause);
  return out;
}

/** A finished sheet: positions in fine cells, or coarse ones when the fine
 *  raster could not take the layout. */
interface FinalSheet {
  placed: Placed[];
  fine: boolean;
}

function toSheet(
  placed: Placed[],
  cellPt: number,
  originPt: number,
  sheetAreaPt2: number
): NestSheet {
  const placements: NestPlacement[] = placed.map((p) => {
    // fine and coarse masks share index, rotation and slot inset
    const m = p.item.fine[p.mi];
    return {
      piece: p.item.piece,
      xPt: originPt + p.x * cellPt + m.slotInsetPt,
      yPt: originPt + p.y * cellPt + m.slotInsetPt,
      rotated: m.rotated,
      rotation: m.rotation,
    };
  });
  const used = placements.reduce(
    (sum, p) => sum + (p.piece.areaPt2 ?? p.piece.widthPt * p.piece.heightPt),
    0
  );
  const garments = [
    ...new Set(
      placements
        .map((p) => p.piece.copyIndex)
        .filter((c): c is number => c !== null && c !== undefined)
    ),
  ].sort((a, b) => a - b);
  return {
    placements,
    utilization: sheetAreaPt2 > 0 ? used / sheetAreaPt2 : 0,
    garments: garments.length ? garments : undefined,
  };
}

/** A yield that lets the browser repaint while the packer runs, and the
 *  point where a cancelled nest stops. */
function makePause(isCancelled?: () => boolean): Pause {
  // MessageChannel, not setTimeout: Chrome throttles chained timers in a
  // hidden tab to one wake per minute, which turned a 8 s nest into a
  // 6-minute one when the user switched tabs.
  const yieldToUi = () =>
    new Promise<void>((resolve) => {
      if (typeof MessageChannel === 'undefined') return void setTimeout(resolve, 0);
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };
      channel.port2.postMessage(null);
    });
  // The clock only decides WHEN to yield, never what is computed.
  let lastYield = performance.now();
  return async () => {
    if (isCancelled?.()) throw new NestCancelledError();
    if (performance.now() - lastYield < 30) return;
    await yieldToUi();
    lastYield = performance.now();
    if (isCancelled?.()) throw new NestCancelledError();
  };
}

/**
 * Shape-aware replacement for packPieces(). Same contract and options; async
 * because silhouettes are flattened with paper.js.
 *
 * Every result is checked exactly before it is returned (nestVerify: real
 * silhouettes through the sheet builders' own placement frame). Should a
 * collision slip through the raster, the nest is redone with a wider raster
 * margin, and as a last resort the rectangle packer's result is used.
 */
export async function nestPieces(
  pieces: NestPieceInput[],
  settings: NestSettings,
  paddingIn?: number,
  options: PackOptions = {}
): Promise<NestResult> {
  const spec = resolveOutlineSpec(settings);
  const pause = makePause(options.isCancelled);
  const memo = new Map();
  /** Everything wrong with a result: a piece placed twice or never (and not
   *  reported oversized), or any exact collision / edge-margin breach. */
  const check = async (result: NestResult): Promise<string[]> => {
    const problems: string[] = [];
    const seen = new Map<NestPieceInput, number>();
    for (const sheet of result.sheets) {
      for (const p of sheet.placements) seen.set(p.piece, (seen.get(p.piece) ?? 0) + 1);
    }
    for (const p of result.oversized) seen.set(p, (seen.get(p) ?? 0) + 1);
    const wrong = pieces.filter((p) => seen.get(p) !== 1);
    if (wrong.length) {
      problems.push(
        `${wrong.length} piece(s) not placed exactly once: ${wrong
          .slice(0, 3)
          .map((p) => `${p.id} x${seen.get(p) ?? 0}`)
          .join(', ')}`
      );
    }
    for (const sheet of result.sheets) {
      const found: SheetViolation[] = await verifySheet(sheet, {
        spec,
        paddingPt: result.paddingPt,
        paperWidthPt: result.paperWidthPt,
        paperLengthPt: result.paperLengthPt,
        pause,
        memo,
      });
      for (const v of found.slice(0, 3)) {
        problems.push(`${v.kind} ${v.a}${v.b ? ` x ${v.b}` : ''} (${v.amount.toFixed(2)})`);
      }
      if (found.length) break;
    }
    return problems;
  };
  for (const safetyPt of [RASTER_SAFETY_PT, RETRY_SAFETY_PT]) {
    const result = await nestOnce(pieces, settings, paddingIn, options, safetyPt, pause);
    const problems = await check(result);
    if (!problems.length) return { ...result, engine: 'shape' };
    // eslint-disable-next-line no-console
    console.warn(
      '[nesting] shape nest failed its own check; nesting again with a wider margin',
      problems
    );
  }
  // Last resort: rectangles. maxrects can push a piece it turned 90 into the
  // edge margin, so a 90-free run is tried when the first one fails too.
  const allowed = settings.allowed_rotations ?? [];
  const attempts = allowed.includes(90)
    ? [settings, { ...settings, allowed_rotations: allowed.filter((r) => r !== 90) }]
    : [settings];
  let fallback: NestResult | null = null;
  for (const attempt of attempts) {
    fallback = { ...packPieces(pieces, attempt, paddingIn, options), engine: 'rect-fallback' };
    const problems = await check(fallback);
    if (!problems.length) return fallback;
    // eslint-disable-next-line no-console
    console.error('[nesting] rectangle fallback failed the exact check', problems);
  }
  return { ...(fallback as NestResult), unverified: true };
}

async function nestOnce(
  pieces: NestPieceInput[],
  settings: NestSettings,
  paddingIn: number | undefined,
  options: PackOptions,
  safetyPt: number,
  maybeYield: Pause
): Promise<NestResult> {
  const effectivePaddingIn = paddingIn ?? settings.piece_padding ?? DEFAULT_PIECE_PADDING_IN;
  const paddingPt = inchToPt(effectivePaddingIn);
  const rotations = allowedRotations(settings);
  const outlineSpec = resolveOutlineSpec(settings);
  const paperWidthPt = inchToPt(settings.paper_width);
  const paperLengthPt = inchToPt(settings.paper_length);
  // The grid starts half a padding outside the edge margin: masks carry that
  // half padding around every outline, and only the OUTLINE must keep the
  // margin clear.
  const borderPt = mmToPt(SHEET_EDGE_MARGIN_MM);
  const originPt = Math.max(0, borderPt - paddingPt / 2);
  const cellPt = mmToPt(CELL_MM);
  const fineCellPt = mmToPt(FINE_CELL_MM);
  const FW = Math.floor((paperWidthPt - 2 * originPt) / fineCellPt);
  // working length per sheet: the paper's, unless that grid would not fit in memory
  const FH = Math.min(
    Math.floor((paperLengthPt - 2 * originPt) / fineCellPt),
    Math.floor(FINE_MAX_CELLS / Math.max(1, FW))
  );
  const W = Math.floor((paperWidthPt - 2 * originPt) / cellPt);
  const H = Math.min(
    Math.floor((paperLengthPt - 2 * originPt) / cellPt),
    Math.floor(FH / FINE_RATIO)
  );
  const sheetArea = paperWidthPt * paperLengthPt;
  /** Full usable length in fine cells (FH may be capped for memory). */
  const fullFH = Math.floor((paperLengthPt - 2 * originPt) / fineCellPt);
  const profile = EFFORT_PROFILES[options.effort ?? 'balanced'] ?? EFFORT_PROFILES.balanced;
  const deadline = performance.now() + profile.deadlineMs;
  const pastDeadline = () => performance.now() > deadline;

  const oversized: NestPieceInput[] = [];
  /** Pieces longer than the memory-capped working length but within the real
   *  paper (only on very wide paper): packed by the rectangle packer. */
  const tooLongForGrid: NestPieceInput[] = [];
  const prepared: Prepared[] = [];
  /** Pieces that fit only on the fine grid (within a coarse cell of the
   *  usable width or length): packed on the fine grid directly. */
  const nearFit: Prepared[] = [];
  const kids = new Map<string, number>();
  const shapeMemo = new Map<string, Promise<{ masks: PieceMask[]; fine: PieceMask[] }>>();
  for (const piece of pieces) {
    const extentPt = outlineExtentPt(piece, outlineSpec);
    const key = `${piece.outlineD ?? 'box'}|${piece.widthPt}|${piece.heightPt}|${extentPt}`;
    let pending = shapeMemo.get(key);
    if (!pending) {
      pending = slotPolygons(piece, extentPt, paddingPt, safetyPt).then((shape) => {
        const fine = makeMasks(shape, rotations, paddingPt, FINE_CELL_MM);
        return { masks: fine.map((f) => coarsen(f, FINE_RATIO)), fine };
      });
      shapeMemo.set(key, pending);
    }
    const { masks: all, fine: allFine } = await pending;
    await maybeYield();
    const keep = all.map((_, i) => i).filter((i) => all[i].w <= W && all[i].h <= H);
    if (!kids.has(key)) kids.set(key, kids.size);
    if (!keep.length) {
      const fineKeep = allFine
        .map((_, i) => i)
        .filter((i) => allFine[i].w <= FW && allFine[i].h <= FH);
      if (!fineKeep.length) {
        const fitsPaper = allFine.some((m) => m.w <= FW && m.h <= fullFH);
        (fitsPaper ? tooLongForGrid : oversized).push(piece);
      } else
        nearFit.push({
          piece,
          key,
          kid: kids.get(key)!,
          masks: [],
          fine: fineKeep.map((i) => allFine[i]),
          area: allFine[fineKeep[0]].count,
        });
      continue;
    }
    prepared.push({
      piece,
      key,
      kid: kids.get(key)!,
      masks: keep.map((i) => all[i]),
      fine: keep.map((i) => allFine[i]),
      area: all[0].count,
    });
  }

  const sheets: NestSheet[] = [];
  const lengthOf = (sh: FinalSheet) =>
    sh.fine
      ? usedLengthPt(sh.placed, fineCellPt, fineMask)
      : usedLengthPt(sh.placed, cellPt, coarseMask);

  /**
   * Compact the pool's layouts (those leaving as many pieces over as the
   * first) and keep the shortest. Returns the sheet AND the pieces that
   * candidate left over: candidates with the same leftover count can leave
   * DIFFERENT pieces, so the next sheet must continue with the chosen one's.
   */
  const finishPool = async (
    pool: Layout[]
  ): Promise<{ sheet: FinalSheet; unplaced: Prepared[] }> => {
    let best: FinalSheet = { placed: pool[0].placed, fine: false };
    let bestUnplaced = pool[0].unplaced;
    let bestLen = lengthOf(best);
    for (const cand of pool) {
      if (cand.unplaced.length !== pool[0].unplaced.length) continue;
      if (pastDeadline()) break;
      const fine = await compact(FW, FH, cand.placed, maybeYield);
      if (fine) {
        const sh = { placed: fine, fine: true };
        const len = lengthOf(sh);
        if (len < bestLen - 1e-6) {
          bestLen = len;
          best = sh;
          bestUnplaced = cand.unplaced;
        }
      }
      await maybeYield();
    }
    return { sheet: best, unplaced: bestUnplaced };
  };

  /** Pack a group sheet by sheet with the beam search. (Every prepared
   *  piece fits an empty sheet, so each sheet places at least one.) */
  const packGroup = async (items: Prepared[], cfg: BeamConfig) => {
    const out: FinalSheet[] = [];
    let remaining = items;
    while (remaining.length) {
      const pool = await beamSheet(W, H, remaining, cfg, maybeYield, profile.pool, pastDeadline);
      if (!pool[0]?.placed.length) break;
      const { sheet, unplaced } = await finishPool(pool);
      out.push(sheet);
      remaining = unplaced;
    }
    return out;
  };

  /** Pack a group with every config of the portfolio; fewest sheets, then
   *  shortest total length wins. */
  const packGroupBest = async (items: Prepared[], portfolio: readonly BeamConfig[]) => {
    let best: FinalSheet[] | null = null;
    const totalLen = (r: FinalSheet[]) => r.reduce((t, sh) => t + lengthOf(sh), 0);
    for (const cfg of portfolio) {
      const run = await packGroup(items, cfg);
      if (
        !best ||
        run.length < best.length ||
        (run.length === best.length && totalLen(run) < totalLen(best) - 1e-6)
      ) {
        best = run;
      }
    }
    return best ?? [];
  };
  const effort = EFFORT_ORDER.includes(options.effort as NestEffort)
    ? (options.effort as NestEffort)
    : 'balanced';
  const portfolioFor = (
    items: Prepared[],
    from = 0,
    budget = profile.groupBudget,
    level: NestEffort = effort
  ): readonly BeamConfig[] => {
    const own = EFFORT_PROFILES[level];
    const n = items.length;
    const types = new Set(items.map((i) => i.kid)).size;
    const orients = items[0]?.masks.length ?? 1;
    const out: BeamConfig[] = [];
    const seen = new Set<string>();
    for (const lower of EFFORT_ORDER.slice(0, EFFORT_ORDER.indexOf(level) + 1)) {
      const p = EFFORT_PROFILES[lower];
      const scaled = (budget * p.groupBudget) / own.groupBudget;
      for (const cfg of pickPortfolio(n, types, orients, from, scaled, p.ladder)) {
        const key = `${cfg.width}:${cfg.pockets}:${cfg.bestFit}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(cfg);
      }
    }
    return out;
  };

  /** Estimated work of a portfolio (same units as BEAM_BUDGET). */
  const portfolioWork = (items: Prepared[], portfolio: readonly BeamConfig[]) => {
    const n = items.length;
    const types = new Set(items.map((i) => i.kid)).size;
    const orients = items[0]?.masks.length ?? 1;
    return portfolio.reduce(
      (t, c) =>
        t +
        (c.width > 0
          ? estimateBeam(n, types, orients, c)
          : n * orients * (c.bestFit ? n * types : 1) * Math.max(1, n / 5)),
      0
    );
  };

  /** The same pieces with only the given orientations (null when a piece
   *  would be left without any). */
  const restrictTo = (items: Prepared[], keep: Rotation[]): Prepared[] | null => {
    const out: Prepared[] = [];
    for (const item of items) {
      const idx = item.fine
        .map((m, i) => (keep.includes(m.rotation) ? i : -1))
        .filter((i) => i >= 0);
      if (!idx.length) return null;
      out.push({
        ...item,
        masks: idx.map((i) => item.masks[i]),
        fine: idx.map((i) => item.fine[i]),
      });
    }
    return out;
  };

  /**
   * A known layout mapped onto another set of the same pieces (identical
   * jersey, or the original items of a restricted variant): same key, same
   * orientation. Null when it does not map one to one — then lay out afresh.
   */
  const mapLayout = (known: FinalSheet[], items: Prepared[]): FinalSheet[] | null => {
    const pool = [...items];
    const out: FinalSheet[] = [];
    for (const sheet of known) {
      const placed: Placed[] = [];
      for (const p of sheet.placed) {
        const k = pool.findIndex((i) => i.key === p.item.key);
        if (k < 0) return null;
        const target = pool.splice(k, 1)[0];
        const rotation = p.item.fine[p.mi]?.rotation;
        const mi = target.fine.findIndex((m) => m.rotation === rotation);
        if (mi < 0) return null;
        placed.push({ item: target, mi, x: p.x, y: p.y });
      }
      out.push({ fine: sheet.fine, placed });
    }
    return pool.length ? null : out;
  };

  const fewerThenShorter = (a: FinalSheet[], b: FinalSheet[] | null) => {
    if (!b) return true;
    if (a.length !== b.length) return a.length < b.length;
    const len = (r: FinalSheet[]) => r.reduce((t, sh) => t + lengthOf(sh), 0);
    return len(a) < len(b) - 1e-6;
  };

  /**
   * One jersey laid out within `budget`. With 90 AND 180 allowed a big
   * jersey's four orientations can price the beam search out entirely (plain
   * greedy), which can be worse than 0/180 alone; so the 0/180 and 0/90
   * subsets are searched too, from what the budget has left, and the best of
   * all wins — allowing more turns never makes the nest worse.
   */
  const searchLevel = async (
    items: Prepared[],
    budget: number,
    level: NestEffort
  ): Promise<FinalSheet[]> => {
    const rotations = new Set(items.flatMap((i) => i.fine.map((m) => m.rotation)));
    const variants: Array<Rotation[] | null> = [null];
    if (rotations.size > 2) {
      for (const sub of [
        [0, 180],
        [0, 90],
      ] as Rotation[][]) {
        if (sub.every((r) => rotations.has(r))) variants.push(sub);
      }
    }
    let best: FinalSheet[] | null = null;
    let left = budget;
    for (const sub of variants) {
      const vItems = sub ? restrictTo(items, sub) : items;
      if (!vItems) continue;
      const portfolio = portfolioFor(vItems, 0, Math.max(0, left), level);
      const run = await packGroupBest(vItems, portfolio);
      left -= portfolioWork(vItems, portfolio);
      const mapped = sub ? mapLayout(run, items) : run;
      if (mapped && fewerThenShorter(mapped, best)) best = mapped;
      if (pastDeadline()) break;
    }
    return best ?? [];
  };

  const searchGroup = async (items: Prepared[], budget: number): Promise<FinalSheet[]> => {
    const best = await searchLevel(items, budget, effort);
    // Fast must not cost whole sheets: a jersey that spills only a short
    // tail although it could fit (its halos cover well under a sheet) is
    // searched again at the rescue level.
    const rescue = profile.rescueWith;
    if (best.length < 2 || pastDeadline()) return best;
    const tail = lengthOf(best[best.length - 1]);
    const cells = items.reduce((t, i) => t + (i.masks[0]?.count ?? 0), 0);
    if (tail > 0.3 * H * cellPt || cells > 0.9 * W * H) return best;
    const full = EFFORT_PROFILES[rescue].groupBudget;
    if (rescue === effort && budget >= full) return best; // already searched in full
    const retry = await searchLevel(items, full, rescue);
    return fewerThenShorter(retry, best) ? retry : best;
  };

  /** Layouts of one jersey; identical jerseys (same piece shapes) reuse the
   *  first one's positions, mapped onto their own pieces. */
  const groupMemo = new Map<string, FinalSheet[]>();
  const layoutGroup = async (items: Prepared[], budget = profile.groupBudget) => {
    const sig = items
      .map((i) => i.key)
      .sort()
      .join('#');
    const known = groupMemo.get(sig);
    if (known) {
      const mapped = mapLayout(known, items);
      if (mapped) return mapped;
    }
    const layouts = await searchGroup(items, budget);
    groupMemo.set(sig, layouts);
    return layouts;
  };
  const signature = (items: Prepared[]) =>
    items
      .map((i) => i.key)
      .sort()
      .join('#');

  /** Whole-order greedy: sheets filled in the given order, bottom-left. */
  const packGreedy = async (
    items: Prepared[],
    order: (a: Prepared, b: Prepared) => number,
    tol: number
  ) => {
    const out: Placed[][] = [];
    let remaining = [...items].sort(order);
    while (remaining.length) {
      const g = new OccGrid(W, H);
      const placed: Placed[] = [];
      const left: Prepared[] = [];
      // occupancy only grows: a kind that did not fit stays out of this sheet
      const failed = new Set<number>();
      for (const item of remaining) {
        if (failed.has(item.kid)) {
          left.push(item);
          continue;
        }
        const b = bestPlacement(g, item, tol);
        await maybeYield();
        if (!b) {
          failed.add(item.kid);
          left.push(item);
          continue;
        }
        g.stamp(item.masks[b.mi], b.x, b.y);
        placed.push({ item, ...b });
      }
      // (every prepared piece fits an empty sheet, so a sheet never stays empty)
      if (!placed.length) break;
      out.push(placed);
      remaining = left;
    }
    return out;
  };

  /**
   * Whole-order refinement, sheet by sheet: compact the sheet on the fine
   * grid, then pull pieces from LATER sheets (last sheet first, largest
   * first) into whatever room the finer raster opened up. A later sheet that
   * empties disappears.
   */
  const refineOrder = async (input: FinalSheet[]): Promise<FinalSheet[]> => {
    // later sheets hand pieces over one by one: keep them as FINE layouts
    const work = input.map((sh) =>
      sh.fine
        ? sh.placed.map((p) => ({ ...p }))
        : sh.placed.map((p) => ({ ...p, x: p.x * FINE_RATIO, y: p.y * FINE_RATIO }))
    );
    const out: FinalSheet[] = [];
    for (let i = 0; i < work.length; i++) {
      if (!work[i].length) continue;
      const g = new OccGrid(FW, FH);
      const fine: Placed[] = [];
      const carry: Placed[] = [];
      // (valid by construction — coarse masks cover fine ones — but never
      // trust it blindly: a piece that does not fit is re-placed or carried)
      for (const p of work[i]) {
        if (g.fits(fineMask(p), p.x, p.y)) {
          g.stamp(fineMask(p), p.x, p.y);
          fine.push(p);
        } else carry.push(p);
      }
      for (const p of carry) {
        const pos = g.findBL(fineMask(p), 0);
        if (pos) {
          g.stamp(fineMask(p), pos.x, pos.y);
          fine.push({ ...p, ...pos });
        } else if (i + 1 < work.length) work[i + 1].push(p);
        else work.push([{ ...p, x: 0, y: 0 }]);
      }
      if (pastDeadline()) {
        // (valid as it stands; compaction and pull-backs are optional)
        out.push({ placed: fine, fine: true });
        continue;
      }
      await squeeze(g, fine, maybeYield);
      const failed = new Set<number>();
      let bottom = 0;
      for (const p of fine) bottom = Math.max(bottom, p.y + fineMask(p).h);
      for (let j = work.length - 1; j > i && j >= work.length - PULL_SOURCES; j--) {
        // from the last sheet anything goes (it shortens or empties it); from
        // a middle sheet only moves that keep this sheet's length
        const limit = j === work.length - 1 ? g.H : bottom;
        if (j === work.length - 2) failed.clear();
        const keep: Placed[] = [];
        for (const p of [...work[j]].sort((a, b) => b.item.area - a.item.area)) {
          if (failed.has(p.item.kid)) {
            keep.push(p);
            continue;
          }
          let best: { mi: number; x: number; y: number } | null = null;
          for (let mi = 0; mi < p.item.fine.length; mi++) {
            const pos = g.findBL(p.item.fine[mi], 0);
            if (pos && (!best || pos.y + p.item.fine[mi].h < best.y + p.item.fine[best.mi].h)) {
              best = { mi, ...pos };
            }
          }
          await maybeYield();
          if (!best || best.y + p.item.fine[best.mi].h > limit) {
            failed.add(p.item.kid);
            keep.push(p);
            continue;
          }
          g.stamp(p.item.fine[best.mi], best.x, best.y);
          fine.push({ item: p.item, ...best });
          bottom = Math.max(bottom, best.y + p.item.fine[best.mi].h);
        }
        work[j] = keep;
      }
      await squeeze(g, fine, maybeYield);
      out.push({ placed: fine, fine: true });
    }
    return out;
  };

  /** Whole order as jersey blocks: each jersey's own beam layout, stacked
   *  first-fit (tallest first) down the sheets and squeezed together. */
  const stackBlocks = async (): Promise<FinalSheet[]> => {
    const groups = garmentGroups(prepared, (p) => p.piece);
    const distinct = new Set(
      groups.map((g) =>
        g.pieces
          .map((i) => i.key)
          .sort()
          .join('#')
      )
    ).size;
    const budget = Math.max(profile.minGroupBudget, profile.blocksBudget / Math.max(1, distinct));
    const blocks: Placed[][] = [];
    for (const g of groups) {
      for (const sh of await layoutGroup(g.pieces, budget)) {
        blocks.push(
          sh.placed.map((p) =>
            sh.fine ? { ...p } : { ...p, x: p.x * FINE_RATIO, y: p.y * FINE_RATIO }
          )
        );
      }
    }
    const extent = (b: Placed[]) => {
      let top = Infinity;
      let bottom = 0;
      for (const p of b) {
        top = Math.min(top, p.y);
        bottom = Math.max(bottom, p.y + fineMask(p).h);
      }
      return { top, height: bottom - top };
    };
    blocks.sort((a, b) => extent(b).height - extent(a).height);
    type Bin = { placed: Placed[]; bottom: number; g: OccGrid | null };
    const bins: Bin[] = [];
    // fine grids of the two most recently used bins stay alive; any other
    // bin's grid is rebuilt from its placements when a block lands there
    const live: Bin[] = [];
    const gridOf = (bin: Bin): OccGrid => {
      if (!bin.g) {
        bin.g = new OccGrid(FW, FH);
        for (const p of bin.placed) bin.g.stamp(fineMask(p), p.x, p.y);
      }
      const at = live.indexOf(bin);
      if (at >= 0) live.splice(at, 1);
      live.push(bin);
      while (live.length > 2) live.shift()!.g = null;
      return bin.g;
    };
    for (const block of blocks) {
      const { top, height } = extent(block);
      let bin = bins.find((b) => b.bottom + height <= FH);
      if (!bin) {
        bin = { placed: [], bottom: 0, g: null };
        bins.push(bin);
      }
      const dy = bin.bottom - top;
      // rows below the bin's lowest piece are empty: the block always fits
      const moved = block.map((p) => ({ ...p, y: p.y + dy }));
      const g = gridOf(bin);
      for (const p of moved) g.stamp(fineMask(p), p.x, p.y);
      bin.placed.push(...moved);
      // only the new block moves (the earlier ones were squeezed when they
      // came; refineOrder squeezes whole sheets at the end) — linear, not
      // quadratic, in blocks per sheet
      if (!pastDeadline()) await squeeze(g, moved, maybeYield);
      let bottom = 0;
      for (const p of bin.placed) bottom = Math.max(bottom, p.y + fineMask(p).h);
      bin.bottom = bottom;
      await maybeYield();
    }
    return bins.map((b) => ({ placed: b.placed, fine: true }));
  };

  /** Near-fit pieces, packed bottom-left on fine-grid sheets of their own. */
  const packNearFit = async (items: Prepared[]): Promise<FinalSheet[]> => {
    // (such pieces are rare and huge; only the current sheet's grid is kept)
    const bins: Placed[][] = [];
    let g: OccGrid | null = null;
    for (const item of [...items].sort((a, b) => b.area - a.area)) {
      let b = g ? bestPlacement(g, item, 0, item.fine) : null;
      if (!b) {
        const fresh = new OccGrid(FW, FH);
        b = bestPlacement(fresh, item, 0, item.fine);
        if (!b) {
          oversized.push(item.piece);
          continue;
        }
        g = fresh;
        bins.push([]);
      }
      g!.stamp(item.fine[b.mi], b.x, b.y);
      bins[bins.length - 1].push({ item, ...b });
      await maybeYield();
    }
    return bins.map((placed) => ({ placed, fine: true }));
  };

  const emit = (layouts: FinalSheet[], group?: string | null) => {
    for (const sh of layouts) {
      sheets.push({
        ...toSheet(sh.placed, sh.fine ? fineCellPt : cellPt, originPt, sheetArea),
        group,
      });
    }
  };

  if (!options.perGarment) {
    const byArea = (a: Prepared, b: Prepared) => b.area - a.area;
    const byHeight = (a: Prepared, b: Prepared) => b.masks[0].h - a.masks[0].h || byArea(a, b);
    const byWidth = (a: Prepared, b: Prepared) => b.masks[0].w - a.masks[0].w || byArea(a, b);
    const total = (r: Placed[][]) => r.reduce((s, sh) => s + usedLengthCells(sh, coarseMask), 0);
    let chosen: Placed[][] | null = null;
    const tryOrder = async (order: (a: Prepared, b: Prepared) => number) => {
      for (const tol of [BLF_Y_TOLERANCE_CELLS, 0]) {
        const run = await packGreedy(prepared, order, tol);
        if (
          !chosen ||
          run.length < chosen.length ||
          (run.length === chosen.length && total(run) < total(chosen))
        ) {
          chosen = run;
        }
      }
    };
    // (a very large order gets fewer greedy variants)
    const big = prepared.length > BIG_ORDER;
    for (const order of big ? [byArea] : [byArea, byHeight, byWidth]) await tryOrder(order);
    // Whatever ended up on the last sheet is what did not fit anywhere else:
    // give those piece types priority (placed first on every sheet) and retry.
    const promoted = new Set<number>();
    for (let round = 0; round < (big ? 1 : profile.promoteRounds) && !pastDeadline(); round++) {
      const best = chosen as Placed[][] | null;
      if (!best || best.length < 2) break;
      const before = promoted.size;
      for (const p of best[best.length - 1]) promoted.add(p.item.kid);
      if (promoted.size === before) break;
      const first = (a: Prepared) => (promoted.has(a.kid) ? 0 : 1);
      await tryOrder((a, b) => first(a) - first(b) || byArea(a, b));
    }
    const greedy = await refineOrder((chosen ?? []).map((placed) => ({ placed, fine: false })));
    // its last sheet holds the leftovers: re-pack them for length
    const last = greedy[greedy.length - 1];
    if (last && !pastDeadline()) {
      const items = last.placed.map((p) => p.item);
      const repacked = await packGroupBest(items, portfolioFor(items, LAST_SHEET_RUNG));
      if (repacked.length === 1 && lengthOf(repacked[0]) < lengthOf(last) - 1e-6) {
        greedy[greedy.length - 1] = repacked[0];
      }
    }
    // Candidate 2: every jersey laid out on its own by the beam search, the
    // jersey blocks then stacked onto sheets (tallest first, first fit) and
    // squeezed together.
    let refined = greedy;
    if (!pastDeadline()) {
      const blocks = await refineOrder(await stackBlocks());
      if (fewerThenShorter(blocks, refined)) refined = blocks;
    }
    // Candidate 3: simply one jersey per sheet (free: the blocks candidate
    // already laid every jersey out). A whole order never ends up worse
    // than its per-jersey nest.
    if (!pastDeadline()) {
      const groups = garmentGroups(prepared, (p) => p.piece);
      const distinct = new Set(groups.map((g) => signature(g.pieces))).size;
      const budget = Math.max(profile.minGroupBudget, profile.blocksBudget / Math.max(1, distinct));
      const perJersey: FinalSheet[] = [];
      for (const g of groups) perJersey.push(...(await layoutGroup(g.pieces, budget)));
      if (fewerThenShorter(perJersey, refined)) refined = perJersey;
    }
    emit(refined);
    if (nearFit.length) emit(await packNearFit(nearFit));
  } else {
    // One sheet per jersey (row x copy), roster order; a jersey that does not
    // fit on one sheet continues on the next, still on its own. Identical
    // jerseys (same pieces) reuse the layout.
    const groups = garmentGroups([...prepared, ...nearFit], (p) => p.piece);
    const distinct = new Set(groups.map((g) => signature(g.pieces.filter((i) => i.masks.length))))
      .size;
    const budget = Math.min(
      profile.groupBudget,
      Math.max(profile.minGroupBudget, profile.orderBudget / Math.max(1, distinct))
    );
    for (const { rowIndex, pieces: items } of groups) {
      const label = garmentLabel(options, rowIndex, items[0]?.piece.size);
      const normal = items.filter((i) => i.masks.length);
      const near = items.filter((i) => !i.masks.length);
      if (normal.length) emit(await layoutGroup(normal, budget), label);
      if (near.length) emit(await packNearFit(near), label);
    }
  }

  // Pieces longer than the capped working length (very wide paper only).
  if (tooLongForGrid.length) {
    const extra = packPieces(tooLongForGrid, settings, paddingIn, options);
    sheets.push(...extra.sheets);
    oversized.push(...extra.oversized);
  }

  return { sheets, oversized, paperWidthPt, paperLengthPt, paddingPt };
}
